import { TEST_IDS } from '@va/constants';
import { useTranslate } from '@va/localization';
import { FilterActionButtons, useFiltersContext } from '@va/shared/feature-filters';
import { Paragraph, paragraphSizes } from '@va/ui/design-system';
import { getResponsiveTooltipHeight } from '@va/util/helpers';
import { useWindowDimensions } from '@va/util/hooks';
import { useAriaListboxPattern } from '@va/util/misc';
import { useCallback, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { EditFilterTemplateTab } from './EditFilterTemplateTab';
import { TemplateListItem } from './TemplateListItem';
import { useGetFilterTemplates } from './api';

export const FilterTemplatesTab = ({ closeDropdown }: { closeDropdown: () => void }) => {
  const [focusedTemplateId, setFocusedTemplateId] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);
  const { reportBlockId, applyConditions } = useFiltersContext();

  const { data = [], mutate, isLoading } = useGetFilterTemplates(reportBlockId);

  const translate = useTranslate();

  const handleFocusChange = useCallback((isFocused: boolean, element: HTMLElement) => {
    setFocusedTemplateId((prev) => (isFocused ? element.id : prev === element.id ? prev : null));
  }, []);

  const { listboxProps } = useAriaListboxPattern<HTMLUListElement>({
    id: 'filter-templates-listbox',
    handleFocusChange,
    enabled: !isEditing,
  });

  const selectedTemplate = useMemo(
    () => data?.find((template) => template.id === selectedTemplateId),
    [data, selectedTemplateId],
  );

  const applyTemplate = useCallback(() => {
    if (!selectedTemplate) return;
    applyConditions(selectedTemplate.conditions);
  }, [applyConditions, selectedTemplate]);

  const handleSubmit = useCallback(() => {
    applyTemplate();
    closeDropdown();
  }, [applyTemplate, closeDropdown]);

  const { height } = useWindowDimensions();

  if (isEditing && selectedTemplate) {
    return (
      <EditFilterTemplateTab
        onClose={() => closeDropdown()}
        onBackClick={() => {
          setIsEditing(false);
        }}
        refreshList={mutate}
        template={selectedTemplate}
      />
    );
  }

  return (
    <div>
      <ul
        data-testid={TEST_IDS.generic.filters.filterTemplatesList}
        style={{ height: getResponsiveTooltipHeight(height) }}
        className='min-h-25 max-h-62.5 overflow-auto scrollbar scrollbar-thumb p-1 space-y-1'
        {...listboxProps}
      >
        {isLoading && <Skeleton className='h-72px' count={3} />}
        {!isLoading && data?.length === 0 && (
          <Paragraph className='p-5 text-center' size={paragraphSizes.tiny}>
            {translate('all.filterTemplates.noTemplateCreated')}
          </Paragraph>
        )}
        {!isLoading &&
          data?.map((template) => {
            return (
              <TemplateListItem
                key={template.id}
                onClick={() => {
                  setSelectedTemplateId(template.id);
                }}
                onClickEdit={() => {
                  setIsEditing(true);
                  setSelectedTemplateId(template.id);
                  setFocusedTemplateId(template.id);
                }}
                selectedTemplateId={selectedTemplateId}
                template={template}
                focused={focusedTemplateId === template.id}
                resetFocus={() => setFocusedTemplateId(null)}
              />
            );
          })}
      </ul>
      <FilterActionButtons onClose={closeDropdown} onSubmit={handleSubmit} />
    </div>
  );
};
